import { css, html, LitElement, nothing } from "lit";
import type { TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import {
    TeqtankCommerce,
    type Commerce,
    type CommerceCart,
    type CommerceDataSource,
    type CommerceEventUnsubscribe,
    type CommerceProductComponent,
} from "@teqtank/commerce-sdk";

@customElement("teqtank-cart")
export class TeqtankCart extends LitElement {
    @property({ attribute: false }) public accessor commerce: Commerce | undefined;
    @property({ attribute: "commerce-key" }) public accessor commerceKey = "demo";
    @property({ attribute: "data-source" }) public accessor dataSource: CommerceDataSource =
        "playground";
    @property({ attribute: "checkout-route" }) public accessor checkoutRoute = "/checkout";
    @property({ attribute: "enrollment-route" }) public accessor enrollmentRoute = "/enrollment";
    @property({ attribute: "show-clear", type: Boolean }) public accessor showClear = true;

    @state() private accessor cart: CommerceCart | undefined;
    @state() private accessor loading = true;
    @state() private accessor busyLineId = "";
    @state() private accessor errorMessage = "";
    @state() private accessor restartingEnrollment = false;

    private ownsCommerce = false;
    private unsubscribe: CommerceEventUnsubscribe | undefined;
    private unsubscribeMarket: CommerceEventUnsubscribe | undefined;
    private unsubscribeEnrollment: CommerceEventUnsubscribe | undefined;
    private unsubscribeCheckout: CommerceEventUnsubscribe | undefined;

    public static override styles = css`
        :host {
            --tt-ink: var(--teqtank-color-text, #16221d);
            --tt-muted: var(--teqtank-color-text-muted, #6b746e);
            --tt-accent: var(--teqtank-color-accent, #728f4f);
            --tt-surface: var(--teqtank-color-surface, #faf8f4);
            --tt-soft: var(--teqtank-color-surface-soft, #eee8df);
            --tt-line: var(--teqtank-color-border, rgba(22, 34, 29, 0.12));
            --tt-display: var(--teqtank-font-heading, Georgia, serif);
            --tt-body: var(--teqtank-font-body, Inter, ui-sans-serif, system-ui, sans-serif);
            display: block;
            container-type: inline-size;
            color: var(--tt-ink);
            font-family: var(--tt-body);
        }

        * {
            box-sizing: border-box;
        }
        button,
        a {
            font: inherit;
        }
        button {
            cursor: pointer;
        }

        .bag {
            min-height: 100%;
            display: flex;
            flex-direction: column;
            padding: clamp(1.25rem, 4cqw, 2rem);
            background: var(--teqtank-cart-background, var(--tt-surface));
            border-radius: var(--teqtank-cart-radius, 0);
        }

        .head {
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            gap: 1rem;
            padding-bottom: 1.25rem;
            border-bottom: 1px solid var(--tt-line);
        }

        .head small {
            color: var(--tt-muted);
            font-size: 0.7rem;
            text-transform: uppercase;
            letter-spacing: 0.1em;
        }
        .head h2 {
            margin: 0.2rem 0 0;
            font: 500 clamp(2rem, 6cqw, 2.8rem)/1 var(--tt-display);
        }

        .clear {
            border: 0;
            background: transparent;
            color: var(--tt-muted);
            font-size: 0.72rem;
            text-decoration: underline;
        }

        .lines {
            flex: 1;
        }

        .line {
            display: grid;
            grid-template-columns: 90px minmax(0, 1fr) auto;
            gap: var(--teqtank-cart-item-gap, 15px);
            padding: 20px 0;
            border-bottom: 1px solid var(--tt-line);
        }

        .image,
        .placeholder {
            width: 90px;
            height: 105px;
            object-fit: var(--teqtank-product-image-fit, contain);
            background: var(--tt-soft);
        }

        .placeholder {
            display: grid;
            place-items: center;
            padding: 0.5rem;
            color: var(--tt-accent);
            text-align: center;
            font: 500 0.8rem var(--tt-display);
        }
        .copy {
            min-width: 0;
        }
        .copy h3 {
            margin: 0.2rem 0 0.35rem;
            font-size: 0.95rem;
        }
        .copy small {
            display: block;
            color: var(--tt-muted);
            font-size: 0.7rem;
        }

        .components {
            margin: 0.65rem 0 0;
            padding-left: 1rem;
            color: var(--tt-muted);
            font-size: 0.72rem;
        }

        .components li {
            margin-top: 0.2rem;
        }
        .components .components {
            margin-top: 0.2rem;
        }

        .qty {
            display: flex;
            align-items: center;
            gap: 9px;
            margin-top: 9px;
        }

        .qty button {
            width: 25px;
            height: 25px;
            border: 1px solid var(--tt-line);
            background: transparent;
            color: var(--tt-ink);
        }

        .qty strong {
            min-width: 1.4rem;
            text-align: center;
            font-size: 0.8rem;
        }

        .qty.locked {
            color: var(--tt-muted);
            font-size: 0.72rem;
        }

        .lock-note {
            margin: 0.65rem 0 0;
            padding: 0.55rem 0.65rem;
            border-radius: 0.55rem;
            background: color-mix(in srgb, var(--tt-accent) 8%, var(--tt-surface));
            color: var(--tt-muted);
            font-size: 0.7rem;
            line-height: 1.4;
        }

        .remove {
            margin-top: 0.65rem;
            padding: 0;
            border: 0;
            background: transparent;
            color: var(--tt-muted);
            font-size: 0.68rem;
            text-decoration: underline;
        }

        .line-total {
            align-self: start;
            white-space: nowrap;
            font-size: 0.82rem;
        }

        .summary {
            padding-top: 1.2rem;
            border-top: 1px solid var(--tt-line);
        }

        .summary-row {
            display: flex;
            justify-content: space-between;
            gap: 1rem;
            margin-bottom: 0.4rem;
            color: var(--tt-muted);
            font-size: 0.78rem;
        }

        .summary-row.total {
            margin-top: 0.8rem;
            padding-top: 0.9rem;
            border-top: 1px solid var(--tt-line);
            color: var(--tt-ink);
            font-size: 1rem;
            font-weight: 800;
        }

        .checkout {
            margin-top: 1rem;
            padding: 0.95rem 1rem;
            display: flex;
            justify-content: center;
            border: 1px solid var(--tt-ink);
            border-radius: 999px;
            background: var(--tt-ink);
            color: white;
            font-weight: 800;
            text-decoration: none;
        }

        .enrollment-management {
            margin-top: 1rem;
            padding: 1rem;
            border: 1px solid color-mix(in srgb, var(--tt-accent) 24%, var(--tt-line));
            border-radius: 0.85rem;
            background: color-mix(in srgb, var(--tt-accent) 6%, var(--tt-surface));
        }

        .enrollment-management span {
            display: block;
            margin-top: 0.25rem;
            color: var(--tt-muted);
            font-size: 0.75rem;
        }

        .restart-enrollment {
            width: 100%;
            min-height: 2.75rem;
            margin-top: 0.8rem;
            border: 1px solid var(--tt-accent);
            border-radius: 999px;
            background: transparent;
            color: var(--tt-ink);
            font-weight: 800;
        }

        .empty,
        .loading,
        .error {
            padding: 2rem 0;
            color: var(--tt-muted);
        }
        .error {
            color: #8b2424;
        }

        @container (max-width: 440px) {
            .bag {
                padding: 1rem;
            }
            .line {
                grid-template-columns: 72px minmax(0, 1fr);
                gap: 11px;
            }
            .image,
            .placeholder {
                width: 72px;
                height: 84px;
            }
            .line-total {
                grid-column: 2;
            }
        }
    `;

    public override connectedCallback(): void {
        super.connectedCallback();
        void this.bind();
    }

    protected override updated(changed: Map<PropertyKey, unknown>): void {
        if (changed.has("commerce")) {
            void this.bind();
        }
    }

    public override disconnectedCallback(): void {
        this.unsubscribe?.();
        this.unsubscribeMarket?.();
        this.unsubscribeEnrollment?.();
        this.unsubscribeCheckout?.();
        if (this.ownsCommerce) {
            this.commerce?.dispose();
        }
        super.disconnectedCallback();
    }

    public async refresh(): Promise<CommerceCart> {
        const runtime = await this.requireCommerce();
        const cart = await runtime.cart.get();
        this.cart = cart;
        return cart;
    }

    protected override render() {
        if (this.loading) {
            return html`<div class="bag" part="shell">
                <div class="loading" part="loading-state">Loading your bag…</div>
            </div>`;
        }

        if (this.errorMessage.length > 0) {
            return html`<div class="bag" part="shell">
                <div class="error" part="error-state">${this.errorMessage}</div>
            </div>`;
        }

        const cart = this.cart;

        if (cart === undefined) {
            return html`<div class="bag" part="shell">
                <div class="empty" part="empty-state">Your bag is ready for a new ritual.</div>
            </div>`;
        }

        return html`
            <section class="bag" part="cart shell">
                <header class="head" part="header">
                    <div>
                        <small part="eyebrow">Your ritual</small>
                        <h2 part="title">Shopping bag</h2>
                    </div>
                    ${this.showClear && cart.lines.length > 0 && !this.hasActiveEnrollmentJourney ? html`<button class="clear" part="clear-action" type="button" @click=${this.clear}>Clear bag</button>` : nothing}
                </header>

                <div class="lines" part="item-list">
                    ${
                        cart.lines.length === 0
                            ? html`<p class="empty" part="empty-state">
                                  Your bag is ready for a new ritual.
                              </p>`
                            : cart.lines.map((line) => this.renderLine(line))
                    }
                </div>

                <footer class="summary" part="summary">
                    <div class="summary-row">
                        <span>Subtotal</span><strong>${cart.totals.subtotal.formatted}</strong>
                    </div>
                    ${cart.totals.discount.amount <= 0 ? nothing : html`<div class="summary-row"><span>Discount</span><strong>−${cart.totals.discount.formatted}</strong></div>`}
                    <div class="summary-row total" part="summary-total">
                        <span>Total</span><strong>${cart.totals.total.formatted}</strong>
                    </div>
                    ${
                        this.hasActiveEnrollmentJourney
                            ? html`<div class="enrollment-management" part="enrollment-management">
                                  <strong>Enrollment order</strong>
                                  <span
                                      >Included items are protected. Start again to choose a
                                      different enrollment pack.</span
                                  >
                                  <button
                                      class="restart-enrollment"
                                      part="restart-enrollment-action"
                                      type="button"
                                      ?disabled=${this.restartingEnrollment}
                                      @click=${this.restartEnrollment}
                                  >
                                      ${
                                          this.restartingEnrollment
                                              ? "Starting again…"
                                              : "Choose a different pack"
                                      }
                                  </button>
                              </div>`
                            : nothing
                    }
                    <a
                        class="checkout"
                        part="primary-action checkout-action"
                        href=${this.checkoutRoute}
                        >Checkout</a
                    >
                </footer>
            </section>
        `;
    }

    private renderLine(line: CommerceCart["lines"][number]) {
        const enrollmentLine = this.enrollmentLine(line.id);
        const enrollmentMetadataPending = this.enrollmentMetadataPending;
        const quantityLocked = enrollmentMetadataPending || enrollmentLine?.quantityLocked;
        const removalLocked = enrollmentMetadataPending || enrollmentLine?.removalLocked;
        return html`<article class="line" part="item">
            ${
                line.image === null
                    ? html`<div class="placeholder">${line.name}</div>`
                    : html`<img
                          class="image"
                          src=${line.image.url}
                          alt=${line.image.alternativeText}
                      />`
            }
            <div class="copy" part="item-content">
                <h3 part="item-title">${line.name}</h3>
                <small
                    >${line.purchaseType === "recurring" ? `Subscribe & save${line.frequency?.description === undefined ? (line.frequencyDays === undefined ? "" : ` · every ${line.frequencyDays} days`) : ` · ${line.frequency.description}`}` : "One-time purchase"}</small
                >
                <small>${line.unitPrice.formatted} each</small>
                ${this.renderComponents(line.components)}
                ${
                    quantityLocked
                        ? html`<div class="qty locked" part="quantity locked-quantity">
                              <span>Quantity</span><strong>${line.quantity}</strong>
                          </div>`
                        : html`<div class="qty" part="quantity">
                              <button
                                  type="button"
                                  ?disabled=${this.busyLineId === line.id}
                                  @click=${() => this.changeQuantity(line.id, line.quantity - 1)}
                              >
                                  −
                              </button>
                              <strong>${line.quantity}</strong>
                              <button
                                  type="button"
                                  ?disabled=${this.busyLineId === line.id}
                                  @click=${() => this.changeQuantity(line.id, line.quantity + 1)}
                              >
                                  +
                              </button>
                          </div>`
                }
                ${
                    removalLocked
                        ? html`<p class="lock-note" part="lock-note">
                              ${
                                  enrollmentMetadataPending
                                      ? "Confirming enrollment item rules…"
                                      : (enrollmentLine?.lockReason ??
                                        "This item is included with your enrollment selection.")
                              }
                          </p>`
                        : html`<button
                              class="remove"
                              part="remove-action"
                              type="button"
                              ?disabled=${this.busyLineId === line.id}
                              @click=${() => this.removeLine(line.id)}
                          >
                              Remove
                          </button>`
                }
            </div>
            <strong class="line-total" part="item-total">${line.lineTotal.formatted}</strong>
        </article>`;
    }

    private renderComponents(
        components: readonly CommerceProductComponent[] | null | undefined,
    ): TemplateResult | typeof nothing {
        if (!components?.length) return nothing;
        return html`
            <div aria-label="Kit contents">
                <small>Includes</small>
                <ul class="components">
                    ${components.map(
                        (component) => html`
                            <li>
                                ${component.quantity} × ${component.name}
                                ${this.renderComponents(component.components)}
                            </li>
                        `,
                    )}
                </ul>
            </div>
        `;
    }

    private async bind(): Promise<void> {
        this.unsubscribe?.();
        this.unsubscribe = undefined;
        this.unsubscribeMarket?.();
        this.unsubscribeMarket = undefined;
        this.unsubscribeEnrollment?.();
        this.unsubscribeEnrollment = undefined;
        this.unsubscribeCheckout?.();
        this.unsubscribeCheckout = undefined;
        this.loading = true;
        this.errorMessage = "";

        try {
            const runtime = await this.requireCommerce();
            this.unsubscribe = runtime.cart.onChanged((event) => {
                this.cart = event.cart;
            });
            this.unsubscribeMarket = runtime.market.onChanged(() => {
                void this.refresh();
            });
            this.unsubscribeEnrollment = runtime.enrollment.onChanged(() => {
                // Enrollment and Cart are two projections of the same Gateway-owned order.
                // Refresh the projection after every Enrollment command as a resilient fallback
                // to the normal cart.changed event.
                void this.refresh();
            });
            this.unsubscribeCheckout = runtime.checkout.onChanged(() => {
                this.requestUpdate();
            });
            this.cart = await runtime.cart.get();
            await runtime.enrollment.restoreIfAvailable();
        } catch (error) {
            this.errorMessage =
                error instanceof Error ? error.message : "The Cart could not be loaded.";
        } finally {
            this.loading = false;
        }
    }

    private async changeQuantity(lineId: string, quantity: number): Promise<void> {
        if (quantity <= 0) {
            await this.removeLine(lineId);
            return;
        }

        const runtime = await this.requireCommerce();
        this.busyLineId = lineId;
        try {
            this.cart = await runtime.cart.updateQuantity({ lineId, quantity });
        } finally {
            this.busyLineId = "";
        }
    }

    private async removeLine(lineId: string): Promise<void> {
        const runtime = await this.requireCommerce();
        this.busyLineId = lineId;
        try {
            this.cart = await runtime.cart.remove(lineId);
        } finally {
            this.busyLineId = "";
        }
    }

    private clear = async (): Promise<void> => {
        const runtime = await this.requireCommerce();
        this.cart = await runtime.cart.clear();
    };

    private get activeEnrollment() {
        const enrollment = this.commerce?.enrollment.current;
        return enrollment?.status === "active" || enrollment?.status === "checkout"
            ? enrollment
            : null;
    }

    private get hasActiveEnrollmentJourney(): boolean {
        return (
            this.activeEnrollment !== null ||
            this.commerce?.checkout.current?.experience === "enrollment"
        );
    }

    private enrollmentLine(lineId: string) {
        return this.activeEnrollment?.cart.lines.find((value) => value.line.id === lineId);
    }

    private get enrollmentMetadataPending(): boolean {
        return this.hasActiveEnrollmentJourney && this.activeEnrollment === null;
    }

    private readonly restartEnrollment = async (): Promise<void> => {
        const runtime = await this.requireCommerce();
        if (this.restartingEnrollment) return;
        this.restartingEnrollment = true;
        this.errorMessage = "";
        try {
            if (
                !runtime.enrollment.current &&
                runtime.checkout.current?.experience === "enrollment"
            ) {
                await runtime.enrollment.resume();
            }
            if (!runtime.enrollment.current) return;
            await runtime.enrollment.abandon();
            await runtime.enrollment.begin({});
            this.dispatchEvent(
                new CustomEvent("teqtank-enrollment-restarted", {
                    bubbles: true,
                    composed: true,
                }),
            );
            const destination = new URL(this.enrollmentRoute, globalThis.location.href);
            if (
                destination.pathname !== globalThis.location.pathname ||
                destination.search !== globalThis.location.search
            ) {
                globalThis.location.assign(destination.href);
            }
        } catch (error) {
            this.errorMessage =
                error instanceof Error ? error.message : "Enrollment could not be restarted.";
        } finally {
            this.restartingEnrollment = false;
        }
    };

    private async requireCommerce(): Promise<Commerce> {
        if (this.commerce !== undefined) {
            return this.commerce;
        }
        this.commerce = await TeqtankCommerce.initialize({
            commerceKey: this.commerceKey,
            dataSource: this.dataSource,
        });
        this.ownsCommerce = true;
        return this.commerce;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "teqtank-cart": TeqtankCart;
    }
}
